import { useCallback, useMemo, useReducer, useState } from "react"
import { FleetEntry } from "../../../api/fleet"
import { makeErrors, makeEditedFleetEntries } from "../editUtils"
import { useChangedAndUnchangedEntries } from "../../confirmChanges/hooks/useChangedAndUnchangedEntries"
import { editCoverDatesReducer, initialState } from "../reducers/editCoverDatesReducer"
import { PolicyDuration } from "../../types"
import { useFleetEntryValidation } from "../../validatation"

export const useEditCoverDates = (
  policyDuration: PolicyDuration,
  hasAdminAccess: boolean,
  rowsToEdit: ReadonlyArray<FleetEntry>,
  isHavenPolicy: boolean,
  isCourier: boolean
) => {
  const editIdSet = useMemo(() => new Set<string>(rowsToEdit.map((row) => row.id ?? ``)), [rowsToEdit])

  const [editFormState, dispatchEditForm] = useReducer(editCoverDatesReducer, initialState)
  const [editedEntries, setEditedEntries] = useState<ReadonlyArray<FleetEntry>>([])

  const { changedEntries, unchangedEntries } = useChangedAndUnchangedEntries(rowsToEdit, editedEntries)
  const [status, setStatus] = useState<`editing` | `confirming` | `confirmed`>(`editing`)
  const { validateOffCoverDate, validateOnCoverDate } = useFleetEntryValidation(
    rowsToEdit,
    policyDuration,
    hasAdminAccess,
    isHavenPolicy,
    isCourier,
    {
      isEdit: true,
    }
  )

  const errors = useMemo(
    () =>
      makeErrors(
        rowsToEdit,
        editIdSet,
        validateOnCoverDate,
        validateOffCoverDate,
        editFormState.onCover,
        editFormState.offCover
      ),
    [rowsToEdit, editIdSet, editFormState.onCover, editFormState.offCover, validateOnCoverDate, validateOffCoverDate]
  )

  // Should added in error checkbox be shown (this allows vehicles to be removed)
  const displayAddedInErrorCheckbox = useMemo(
    () =>
      hasAdminAccess &&
      editFormState.onCover !== undefined &&
      editFormState.offCover !== undefined &&
      editFormState.onCover === editFormState.offCover,
    [hasAdminAccess, editFormState.onCover, editFormState.offCover]
  )

  const primaryModalButtonDisabled = useMemo(() => {
    if (status === `confirmed`) return true
    const addedInErrorCheckboxClicked = editFormState.addedInError && displayAddedInErrorCheckbox
    if (status === `confirming`) return !changedEntries.length && !addedInErrorCheckboxClicked

    return (
      (!!errors.length ||
        !(editFormState.offCover || editFormState.onCover) ||
        editFormState.onCoverInvalid ||
        editFormState.offCoverInvalid) &&
      !addedInErrorCheckboxClicked
    )
  }, [
    status,
    changedEntries.length,
    errors.length,
    editFormState.offCover,
    editFormState.onCover,
    editFormState.onCoverInvalid,
    editFormState.offCoverInvalid,
    editFormState.addedInError,
    displayAddedInErrorCheckbox,
  ])

  // The action for next (pre-confirm)
  const onFinishedEditing = useCallback(() => {
    setEditedEntries(
      makeEditedFleetEntries(
        rowsToEdit,
        editIdSet,
        editFormState.onCover,
        editFormState.offCover
      ) as unknown as ReadonlyArray<FleetEntry>
    )
    setStatus(`confirming`)
  }, [rowsToEdit, editIdSet, editFormState.offCover, editFormState.onCover])

  const onCancelConfirm = (): void => {
    dispatchEditForm({ type: `cancelEdit` })
    setStatus(`editing`)
  }

  return {
    errors,
    displayAddedInErrorCheckbox,
    status,
    primaryModalButtonDisabled,
    changedEntries,
    unchangedEntries,
    editFormState,
    editIdSet,
    editedEntries,
    dispatchEditForm,
    onFinishedEditing,
    onCancelConfirm,
    onConfirm: () => setStatus(`confirmed`),
  }
}
